import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Box, Button, Group, Loader, Select, Stack, Switch, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { z } from 'zod';
import { DICTIONARY_CATEGORIES } from '@seo/contracts';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { useCan } from '../lib/session';
import { zodResolver } from '../lib/zod-resolver';
import { CoverageBar } from '../components/CoverageBar';
import { DataTable, type Column } from '../components/DataTable';
import { GeoPresetEditor, type EditableGeoPreset } from './GeoPresetEditor';
import { ParsePanel } from '../components/ParsePanel';
import classes from './EntityPage.module.css';

interface GeoRow {
  id: string;
  countryCode: string;
  countryName: string;
  languages: string[];
  primaryLanguage: string;
  currencyCode: string;
  minAge: number;
  hideBonusBlocks: boolean;
  regulatorName: string | null;
  sitesCount: number;
}

interface CoverageEntry {
  language: string;
  total: number;
  reviewed: number;
  /** Скільки термінів мають переклад — не те саме, що перевірені. */
  translated: number;
  coverage: number;
  byCategory: { category: string; total: number; reviewed: number }[];
}

/** Форма ручного додавання терміна словника — «+ термін вручну» з мокапа. */
const dictionaryTermSchema = z.object({
  language: z.string().min(2, 'Оберіть мову').max(3),
  category: z.enum(DICTIONARY_CATEGORIES),
  termEn: z.string().trim().min(1, 'Вкажіть термін англійською').max(200),
  translation: z.string().trim().max(500),
  context: z.string().trim().max(500),
  reviewed: z.boolean(),
});
type DictionaryTermFormValues = z.infer<typeof dictionaryTermSchema>;

/**
 * ГЕО та мовні пресети (acceptance scenario 3).
 *
 * The row that matters is not the country name — it is the pair of columns that make the
 * preset's power visible: which legal switches are on, and whether the dictionary is
 * complete enough to generate anything at all.
 */
export function GeoPage(): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  /** Пресет-зразок для дублювання: форма створення, але з уже заповненими полями. */
  const [duplicateOf, setDuplicateOf] = useState<EditableGeoPreset | null>(null);
  /** Мова, під яку оператор дивиться готовність і параметри збірки — окрема від картки. */
  const [buildLang, setBuildLang] = useState<string>('');
  const canWriteGeo = useCan('geo_dictionaries', 'write');

  const list = useQuery(
    trpc.geo.list.queryOptions({
      ...(search ? { search } : {}),
      pagination: { limit: 100, direction: 'asc' },
    }),
  );

  // Реєстр не платить за ~20 текстових полів на рядок, які показує лише відкрита картка —
  // деталі йдуть окремим запитом за id, як `brands.byId` для `BrandsPage`.
  const detail = useQuery({
    ...trpc.geo.presets.byId.queryOptions({ id: selectedId ?? '' }),
    enabled: selectedId !== null,
  });

  const coverage = useQuery({
    ...trpc.geo.coverage.queryOptions({ geoId: selectedId ?? '' }),
    enabled: selectedId !== null,
  });

  /**
   * `geo.readiness` і `geo.resolveForBuild` існували в API без жодного викликача з UI —
   * оператор міг лише здогадуватись, чи піде генерація і що саме побачить збірка. Обидва
   * питають про той самий (гео, мова), тож ділять один вибір мови.
   */
  const readiness = useQuery({
    ...trpc.geo.readiness.queryOptions({ geoId: selectedId ?? '', language: buildLang }),
    enabled: selectedId !== null && buildLang !== '',
  });
  const buildPreview = useQuery({
    ...trpc.geo.resolveForBuild.queryOptions({ geoId: selectedId ?? '', language: buildLang }),
    enabled: selectedId !== null && buildLang !== '',
  });

  const dictForm = useForm<DictionaryTermFormValues>({
    initialValues: {
      language: '',
      category: 'seo',
      termEn: '',
      translation: '',
      context: '',
      reviewed: false,
    },
    validate: zodResolver(dictionaryTermSchema),
  });

  /** Яку мову зараз перекладає модель — щоб «завантаження» стояло на своїй кнопці. */
  const [translatingLang, setTranslatingLang] = useState<string | null>(null);
  const translateDict = useMutation(
    trpc.geo.dictionary.translate.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.geo.pathKey() });
      },
    }),
  );

  const addDictionaryTerm = useMutation(
    trpc.geo.dictionary.upsert.mutationOptions({
      onSuccess: async () => {
        dictForm.reset();
        await queryClient.invalidateQueries({ queryKey: trpc.geo.pathKey() });
      },
    }),
  );

  const columns: Column<GeoRow>[] = [
    {
      key: 'country',
      header: 'Країна',
      render: (row) => (
        <Group gap={8} wrap="nowrap">
          <span className={classes.mono}>{row.countryCode}</span>
          <span>{row.countryName}</span>
        </Group>
      ),
    },
    {
      key: 'languages',
      header: 'Мови',
      render: (row) => (
        <Group gap={4}>
          {row.languages.map((lang) => (
            <Badge
              key={lang}
              size="sm"
              variant={lang === row.primaryLanguage ? 'filled' : 'default'}
              data-testid={`geo-lang-${row.countryCode}-${lang}`}
            >
              {lang}
            </Badge>
          ))}
        </Group>
      ),
    },
    {
      key: 'currency',
      header: 'Валюта',
      render: (row) => <span className={classes.mono}>{row.currencyCode}</span>,
    },
    {
      key: 'regulator',
      header: 'Регулятор',
      render: (row) => <span>{row.regulatorName ?? '—'}</span>,
    },
    {
      key: 'age',
      header: 'Вік',
      align: 'right',
      render: (row) => (
        <span className={classes.mono} data-testid={`geo-age-${row.countryCode}`}>
          {row.minAge}+
        </span>
      ),
    },
    {
      key: 'legal',
      header: 'Юридичне',
      render: (row) =>
        row.hideBonusBlocks ? (
          <Badge color="rose" size="sm" data-testid={`geo-nobonus-${row.countryCode}`}>
            бонуси приховано
          </Badge>
        ) : (
          <Text className={classes.dim}>—</Text>
        ),
    },
    {
      key: 'sites',
      header: 'Сайтів',
      align: 'right',
      render: (row) => <span className={classes.mono}>{row.sitesCount}</span>,
    },
  ];

  const closeSelection = (): void => {
    setSelectedId(null);
    setBuildLang('');
  };

  return (
    <Stack gap="md">
      <Group gap="sm">
        <TextInput
          placeholder="країна…"
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
          }}
          data-testid="geo-search"
          style={{ maxWidth: 260 }}
        />
        <Text className={classes.dim} data-testid="geo-count">
          {list.data ? `${String(list.data.items.length)} пресетів` : ''}
        </Text>
      </Group>

      {/*
       * `GeoPresetEditor` без `row` — та сама картка, що редагує вибраний рядок, лише з
       * порожніми значеннями. Реєстр раніше заводив пресет окремою формою на сім полів, а
       * решту ~20 (валютний формат, ліміти виводу, KYC, дисклеймер, заборонені механіки)
       * можна було додати лише другим кроком, відкривши картку щойно створеного рядка —
       * одна форма для обох означає, що поле більше не можна забути на одному з двох шляхів.
       */}
      {createOpen || duplicateOf !== null ? (
        <GeoPresetEditor
          key={duplicateOf?.id ?? 'new'}
          {...(duplicateOf === null ? {} : { duplicateOf })}
          onClose={() => {
            setCreateOpen(false);
            setDuplicateOf(null);
          }}
        />
      ) : canWriteGeo ? (
        <Button
          size="xs"
          data-testid="create-geo-preset-open"
          onClick={() => {
            // Один редактор одночасно: інакше testid-и картки (`edit-geo-preset-*`)
            // дублювались би між формою створення і формою вибраного рядка.
            closeSelection();
            setDuplicateOf(null);
            setCreateOpen(true);
          }}
        >
          Додати ГЕО-пресет
        </Button>
      ) : null}

      <DataTable
        rows={list.data?.items ?? []}
        columns={columns}
        loading={list.isPending}
        error={list.error}
        emptyMessage="Пресетів немає. Створіть перший — і сайти зможуть на нього спертися."
        onRowClick={(row) => {
          setCreateOpen(false);
          setSelectedId(row.id);
          setBuildLang('');
        }}
        rowTestId={(row) => `geo-row-${row.countryCode}`}
        testId="geo-table"
      />

      {selectedId === null ? null : detail.isPending ? (
        <Loader size="sm" data-testid="geo-detail-loading" />
      ) : detail.data === undefined ? (
        <Text className={classes.danger} data-testid="geo-detail-error" role="alert">
          {publicMessageOf(detail.error)}
        </Text>
      ) : (
        <>
          {/*
           * Картка редагування над словником: рядок реєстру відкриває і те, що в ньому
           * записано, і покриття словника — дві половини однієї відповіді «що з цим ГЕО».
           */}
          <GeoPresetEditor key={detail.data.id} row={detail.data} onClose={closeSelection} />

          {/*
            * «AI-ПАРСИНГ ГЕО» і «ДУБЛЮВАТИ ПРЕСЕТ» — дві дії мокапа (рядок 1136), яких екран
            * не мав, хоч обидві спираються на готове.
            *
            * Парсер уміє ринок нарівні з брендом: `geo` є в `PARSE_ENTITY_TYPES`, а
            * `PARSABLE_FIELD_HINTS.geo` перелічує вісім полів — від валюти до дисклеймера.
            * Панель та сама, що на картці бренда: політика перезапису, впевненість, відкат.
            * Друга копія розійшлася б із першою саме в запобіжниках.
            *
            * Дублювання — без нової процедури: відкривається порожня картка створення з уже
            * підставленими значеннями обраного. Ринки-сусіди відрізняються двома полями з
            * двадцяти семи, і переписувати решту руками — те, від чого ця кнопка й рятує.
            */}
          <Group gap="sm">
            {canWriteGeo ? (
              <Button
                size="xs"
                variant="default"
                data-testid="geo-duplicate"
                onClick={() => {
                  setDuplicateOf(detail.data ?? null);
                  closeSelection();
                }}
              >
                Дублювати пресет
              </Button>
            ) : null}
          </Group>

          <ParsePanel
            entityType="geo"
            entityId={detail.data.id}
            targets={['currencyCode', 'minAge', 'payoutTime', 'mandatoryDisclaimer']}
          />

          <Box className={classes.panel} data-testid="geo-detail">
            <Group justify="space-between" mb="sm">
              <Text className={classes.panelTitle}>
                Словник · {detail.data.countryName} ({detail.data.countryCode})
              </Text>
              <Text className={classes.dim}>покриття рахується лише за перевіреними перекладами</Text>
            </Group>

            {coverage.isPending ? (
              <Loader size="sm" />
            ) : (
              <Stack gap="sm">
                {(coverage.data as CoverageEntry[] | undefined)?.map((entry) => (
                  <Box key={entry.language} data-testid={`coverage-${entry.language}`}>
                    <Group justify="space-between" mb={4}>
                      <Group gap={8}>
                        <Badge size="sm" variant="default">
                          {entry.language}
                        </Badge>
                        <Text className={classes.dim}>
                          {entry.reviewed} / {entry.total} термінів
                        </Text>
                      </Group>
                      <Text className={classes.mono} data-testid={`coverage-pct-${entry.language}`}>
                        {entry.coverage}%
                      </Text>
                    </Group>
                    <CoverageBar value={entry.coverage} />
                    {/*
                      * «AI-ДОПЕРЕКЛАСТИ N» — дія мокапа (рядок 1136) над цим самим рядком
                      * покриття. N — саме **неперекладені** терміни, а не «до 100%»: модель
                      * заповнює порожні клітинки, а `reviewed` лишає `false`, бо покриття
                      * рахується по перевірених. Інакше одна кнопка відчиняла б гейт
                      * генерації, не показавши жодного перекладу людині.
                      */}
                    {canWriteGeo && entry.total > entry.translated ? (
                      <Group gap="sm" mt={4}>
                        <Button
                          size="compact-xs"
                          variant="default"
                          loading={translateDict.isPending && translatingLang === entry.language}
                          data-testid={`dict-translate-${entry.language}`}
                          onClick={() => {
                            setTranslatingLang(entry.language);
                            translateDict.mutate({
                              geoId: detail.data?.id ?? '',
                              language: entry.language,
                            });
                          }}
                        >
                          AI-доперекласти {entry.total - entry.translated}
                        </Button>
                        <Text className={classes.dim}>
                          переклад лишається неперевіреним — покриття підніме людина
                        </Text>
                      </Group>
                    ) : null}
                  </Box>
                ))}
                {translateDict.error === null ? null : (
                  <Text className={classes.danger} data-testid="dict-translate-error" role="alert">
                    {publicMessageOf(translateDict.error)}
                  </Text>
                )}
                {translateDict.data === undefined ? null : (
                  <Text className={classes.dim} data-testid="dict-translate-result">
                    перекладено {translateDict.data.translated}
                    {translateDict.data.skipped.length === 0
                      ? ''
                      : ` · модель не взялася за ${translateDict.data.skipped.length}: ${translateDict.data.skipped.join(', ')}`}
                  </Text>
                )}
              </Stack>
            )}

            {/*
             * `geo.dictionary.upsert` існував без викликача — словник наповнювався лише
             * сідом, і покриття, яке блокує генерацію (`assertReadyForGeneration`), не мало
             * способу зрости з екрана.
             */}
            {canWriteGeo ? (
              <form
                data-testid="geo-dict-term-form"
                onSubmit={dictForm.onSubmit((values) => {
                  addDictionaryTerm.mutate({
                    geoId: detail.data.id,
                    language: values.language,
                    category: values.category,
                    termEn: values.termEn,
                    translation: values.translation.trim() || null,
                    context: values.context.trim() || null,
                    reviewed: values.reviewed,
                  });
                })}
              >
                <Group gap="sm" align="flex-end" mt="md" wrap="wrap">
                  <Select
                    size="xs"
                    label="Мова"
                    data={detail.data.languages.map((lang) => ({ value: lang, label: lang }))}
                    allowDeselect={false}
                    data-testid="geo-dict-term-lang"
                    style={{ width: 90 }}
                    {...dictForm.getInputProps('language')}
                  />
                  <Select
                    size="xs"
                    label="Категорія"
                    data={DICTIONARY_CATEGORIES.map((c) => ({ value: c, label: c }))}
                    allowDeselect={false}
                    data-testid="geo-dict-term-category"
                    style={{ width: 130 }}
                    {...dictForm.getInputProps('category')}
                  />
                  <TextInput
                    size="xs"
                    label="Термін (EN)"
                    data-testid="geo-dict-term-en"
                    style={{ width: 160 }}
                    {...dictForm.getInputProps('termEn')}
                  />
                  <TextInput
                    size="xs"
                    label="Переклад"
                    data-testid="geo-dict-term-translation"
                    style={{ width: 160 }}
                    {...dictForm.getInputProps('translation')}
                  />
                  <TextInput
                    size="xs"
                    label="Контекст"
                    data-testid="geo-dict-term-context"
                    style={{ width: 140 }}
                    {...dictForm.getInputProps('context')}
                  />
                  <Switch
                    size="xs"
                    label="Перевірено"
                    data-testid="geo-dict-term-reviewed"
                    {...dictForm.getInputProps('reviewed', { type: 'checkbox' })}
                  />
                  <Button
                    size="xs"
                    type="submit"
                    loading={addDictionaryTerm.isPending}
                    data-testid="geo-dict-term-submit"
                  >
                    + термін вручну
                  </Button>
                </Group>
                {addDictionaryTerm.error == null ? null : (
                  <Text className={classes.danger} mt={6} data-testid="geo-dict-term-error" role="alert">
                    {publicMessageOf(addDictionaryTerm.error)}
                  </Text>
                )}
              </form>
            ) : null}

            <Group gap="sm" align="flex-end" mt="md">
              <Select
                size="xs"
                label="Перевірити готовність до збірки"
                placeholder="мова"
                data={detail.data.languages.map((lang) => ({ value: lang, label: lang }))}
                clearable
                value={buildLang || null}
                onChange={(value) => {
                  setBuildLang(value ?? '');
                }}
                data-testid="geo-build-lang"
                style={{ width: 130 }}
              />
            </Group>

            {buildLang === '' ? null : (
              <Stack gap={6} mt="sm">
                {readiness.isPending ? (
                  <Loader size="xs" />
                ) : readiness.data === undefined ? null : readiness.data.ready ? (
                  <Badge color="mint" size="sm" data-testid="geo-readiness-result">
                    готово до генерації · покриття {readiness.data.coverage}%
                  </Badge>
                ) : (
                  <Badge color="rose" size="sm" data-testid="geo-readiness-result">
                    не готово:{' '}
                    {readiness.data.reason === 'GEO_LANGUAGE_NOT_ALLOWED'
                      ? 'мова не дозволена для цього пресету'
                      : `покриття словника нижче порогу`}
                  </Badge>
                )}

                {/* `resolveForBuild` — те, що збірка бачить як «пресет готовий» (§2 крок 3). */}
                {buildPreview.isPending ? (
                  <Loader size="xs" />
                ) : buildPreview.data !== undefined ? (
                  <Text size="sm" data-testid="geo-build-preview">
                    Збірка побачить: {buildPreview.data.currencyCode} · дисклеймер «
                    {buildPreview.data.mandatoryDisclaimer}» · регулятор{' '}
                    {buildPreview.data.regulator?.name ?? '—'} · {buildPreview.data.paymentMethodCount}{' '}
                    платіжок
                  </Text>
                ) : (
                  <Text className={classes.danger} data-testid="geo-build-preview-error" role="alert">
                    {publicMessageOf(buildPreview.error)}
                  </Text>
                )}
              </Stack>
            )}
          </Box>
        </>
      )}
    </Stack>
  );
}
